import type { TypedArray } from '@kitware/vtk.js/types';

import type { Point, PointSiArray } from '@/src/store/contours/types/contour-definition-array-type';

import { defineStore } from 'pinia';

import { useMaskExtractor } from '@/src/composables/useMaskExtractor';
import { useActiveStudyStore } from '@/src/store/active-study';
import { useAnalysisStore } from '@/src/store/analysis';
import { BiomarkerMask } from '@/src/store/analysis/biomarker-mask';
import { useContourContainerStore } from '@/src/store/contours/contour-container-store';
import {
  ContourLabel,
  encodeContourLabelItemId,
} from '@/src/store/contours/contour-definitions';
import { useViewportStateStore } from '@/src/store/contours/viewport-state-store';
import { Orientation } from '@/src/store/dicom-web/classification-typing';
import { useCurrentStudyStore } from '@/src/store/dicom-web/current-study-store';
import { seriesClassification } from '@/src/store/dicom-web/SeriesClassification';

// ─── Interfaces ─────────────────────────────────────────────────────────────

export interface OcgCalculationProgress {
  current: number;
  total: number;
  percent: number;
  phaseIndex: number;
  stage: string;
  partialResult?: OcgCurvesResult;
}

export type OcgCurveDisplayMode = 'raw' | 'normalized' | 'mask-matched';

interface OcgCalculatorState {
  ocgCurvesCache: Record<string, OcgCurvesResult>;
  ocgCurvesByViewport: Record<string, OcgCurvesResult>;
  isCalculating: boolean;
  curveDisplayMode: OcgCurveDisplayMode;
  isNormalized: boolean;
  progress: OcgCalculationProgress;
  error: string | null;
}

export interface OcgCalculationOptions {
  /** Savitzky-Golay filter window size (odd integer, default 5) */
  sgoKrn?: number;
  /** Number of segments (default 6 for standard AHA basal/mid SAX slices) */
  numberSegments?: number;
  /** Transmurality offset for endocardium (default 0) */
  endoOffset?: number;
  /** Transmurality offset for epicardium (default 0) */
  epiOffset?: number;
  /** Optional callback for tracking computation progress */
  onProgress?: (progress: OcgCalculationProgress) => void;
}

export interface OcgCurvesResult {
  viewerID: string;
  seriesUID: string;
  seriesIndex: number;
  sliceIndex: number;
  startPhase: number;
  endPhase: number;
  nPhases: number;
  imageUIDs: string[];
  triggerTimes: number[];
  excludedPhaseIndices: number[];
  edMyoVal?: number;

  // 6 AHA segments per phase: [phaseIndex (0..nPh-1)][segmentIndex (0..5)]
  siAHA16: number[][];

  // Coronary territories (per phase):
  siLAD: number[]; // Mean of segments 0 & 1 (AHA segments 1 & 2)
  siRCA: number[]; // Mean of segments 2 & 3 (AHA segments 3 & 4)
  siLCx: number[]; // Mean of segments 4 & 5 (AHA segments 5 & 6)
  siGlobal: number[]; // Median of whole myocardium signal intensity

  // Smoothed curves (Savitzky-Golay smoothed):
  siLADSmoothed: number[];
  siRCASmoothed: number[];
  siLCxSmoothed: number[];
  siGlobalSmoothed: number[];

  // Normalized curves (normalized to start at 1.0):
  siLADNormalized: number[];
  siRCANormalized: number[];
  siLCxNormalized: number[];
  siGlobalNormalized: number[];

  // Mask-Matched curves (normalized by global Phase 0 ED Myocardium median edMyoVal):
  siLADMaskMatched: number[];
  siRCAMaskMatched: number[];
  siLCxMaskMatched: number[];
  siGlobalMaskMatched: number[];

  // Cardiac Phase & Salient Points (End-Systole / End-Diastole):
  lvBpArea: number[]; // Raw LV blood pool pixel count per phase
  lvBpAreaSmoothed: number[]; // Filtered LV blood pool area signal (filtfilt)
  esIndices: number[]; // End-systole phase indices (0-indexed local minima of cavity area)
  edIndices: number[]; // End-diastole phase indices (0-indexed local maxima of cavity area + endpoints)
  nRR: number; // Number of cardiac cycles detected
}

const laxOrientations = new Set<Orientation>([
  Orientation.Lax2CV,
  Orientation.Lax3CV,
  Orientation.Lax4CV,
  Orientation.Lax5CV,
]);

function isLaxOrientation(orientation: Orientation): boolean {
  return laxOrientations.has(orientation);
}

function createFilledArray<T>(length: number, value: T): T[] {
  const result: T[] = [];
  result.length = length;
  return result.fill(value);
}

// ─── Pure Math & Image Processing Algorithms ────────────────────────────────

/**
 * Calculates the median of an array of numbers, ignoring NaNs.
 */
export function calculateMedian(values: number[]): number {
  const valid = values.filter(v => typeof v === 'number' && Number.isFinite(v) && !Number.isNaN(v));
  if (valid.length === 0) return Number.NaN;
  valid.sort((a, b) => a - b);
  const mid = Math.floor(valid.length / 2);
  return valid.length % 2 !== 0 ? valid[mid] : (valid[mid - 1] + valid[mid]) / 2;
}

/**
 * Calculates the mean of an array of numbers, ignoring NaNs.
 */
export function calculateMean(values: number[]): number {
  const valid = values.filter(v => typeof v === 'number' && Number.isFinite(v) && !Number.isNaN(v));
  if (valid.length === 0) return Number.NaN;
  const sum = valid.reduce((acc, curr) => acc + curr, 0);
  return sum / valid.length;
}

/**
 * Optimized division of a 2D blob into `n` sectors relative to a reference user point.
 * Matches backend `divide_blob_optimized(blob_image, n, user_point)`.
 *
 * @param blobPoints Coordinates of pixels in the LV blob (blood pool + myocardium)
 * @param numSegments Number of segments to divide the blob into (e.g. 6)
 * @param userPoint Reference point (e.g. RV insertion point: { x, y })
 * @returns Map/function to get segment index (1..n) for any (x, y) coordinate
 */
export function divideBlobOptimized(
  blobPoints: Point[],
  numSegments: number,
  userPoint: Point,
): {
  centroid: Point;
  vecAngle: number;
  getSegment: (x: number, y: number) => number;
} {
  if (blobPoints.length === 0) {
    return {
      centroid: { x: 0, y: 0 },
      vecAngle: 0,
      getSegment: () => 0,
    };
  }

  // Find centroid of the blob (np.mean(y_indices), np.mean(x_indices))
  let sumX = 0;
  let sumY = 0;
  for (let i = 0; i < blobPoints.length; i++) {
    sumX += blobPoints[i].x;
    sumY += blobPoints[i].y;
  }
  const centroid: Point = {
    x: sumX / blobPoints.length,
    y: sumY / blobPoints.length,
  };

  // Calculate the angle of the vector from centroid to user point
  // vec_angle = np.arctan2(user_point[1] - centroid[0], user_point[0] - centroid[1])
  const dyVec = userPoint.y - centroid.y;
  const dxVec = userPoint.x - centroid.x;
  const rawVecAngle = Math.atan2(dyVec, dxVec);
  const vecAngle = ((rawVecAngle % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);

  const angleStep = (2 * Math.PI) / numSegments;

  const getSegment = (x: number, y: number): number => {
    const dy = y - centroid.y;
    const dx = x - centroid.x;
    const rawAngle = Math.atan2(dy, dx);
    const angle = ((rawAngle % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
    const normalizedAngle = ((angle - vecAngle) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI);
    const segIdx = Math.floor(normalizedAngle / angleStep);
    return ((numSegments - segIdx) % numSegments) + 1; // 1 to numSegments
  };

  return {
    centroid,
    vecAngle,
    getSegment,
  };
}

/**
 * Linearly interpolates NaNs in a 2D array of dimensions [nPhases, nSegments]
 * across the temporal (phase) axis.
 */
export function interpolateNaNs(data: number[][]): number[][] {
  const nPhases = data.length;
  if (nPhases === 0) return [];
  const nSegments = data[0].length;

  const result: number[][] = Array.from({ length: nPhases }, () =>
    createFilledArray(nSegments, 0));

  for (let s = 0; s < nSegments; s++) {
    const validPhases: number[] = [];
    for (let p = 0; p < nPhases; p++) {
      const val = data[p][s];
      if (typeof val === 'number' && Number.isFinite(val) && !Number.isNaN(val)) {
        validPhases.push(p);
      }
    }

    if (validPhases.length === 0) {
      for (let p = 0; p < nPhases; p++) {
        result[p][s] = 0;
      }
    }
    else if (validPhases.length === 1) {
      const singleVal = data[validPhases[0]][s];
      for (let p = 0; p < nPhases; p++) {
        result[p][s] = singleVal;
      }
    }
    else {
      for (let p = 0; p < nPhases; p++) {
        const val = data[p][s];
        if (typeof val === 'number' && Number.isFinite(val) && !Number.isNaN(val)) {
          result[p][s] = val;
          continue;
        }

        // Extrapolate before first valid phase
        if (p < validPhases[0]) {
          result[p][s] = data[validPhases[0]][s];
          continue;
        }

        // Extrapolate after last valid phase
        if (p > validPhases[validPhases.length - 1]) {
          result[p][s] = data[validPhases[validPhases.length - 1]][s];
          continue;
        }

        // Linearly interpolate between surrounding valid phases
        let prevIdx = validPhases[0];
        let nextIdx = validPhases[validPhases.length - 1];
        for (let i = 0; i < validPhases.length - 1; i++) {
          if (validPhases[i] <= p && p <= validPhases[i + 1]) {
            prevIdx = validPhases[i];
            nextIdx = validPhases[i + 1];
            break;
          }
        }

        const t = (p - prevIdx) / (nextIdx - prevIdx);
        result[p][s] = data[prevIdx][s] + t * (data[nextIdx][s] - data[prevIdx][s]);
      }
    }
  }

  return result;
}

/**
 * Linearly interpolates NaNs in a 1D array across the index axis.
 */
export function interpolateNaNs1D(data: number[]): number[] {
  const n = data.length;
  if (n === 0) return [];
  const validIndices: number[] = [];
  for (let i = 0; i < n; i++) {
    const val = data[i];
    if (typeof val === 'number' && Number.isFinite(val) && !Number.isNaN(val)) {
      validIndices.push(i);
    }
  }

  if (validIndices.length === 0) {
    return createFilledArray(n, 0);
  }
  if (validIndices.length === 1) {
    const fillValue = data[validIndices[0]];
    return createFilledArray(n, fillValue);
  }

  const result = createFilledArray(n, 0);
  for (let i = 0; i < n; i++) {
    const val = data[i];
    if (typeof val === 'number' && Number.isFinite(val) && !Number.isNaN(val)) {
      result[i] = val;
      continue;
    }
    // Extrapolate before first valid index
    if (i < validIndices[0]) {
      result[i] = data[validIndices[0]];
      continue;
    }
    // Extrapolate after last valid index
    if (i > validIndices[validIndices.length - 1]) {
      result[i] = data[validIndices[validIndices.length - 1]];
      continue;
    }
    // Interpolate between surrounding valid indices
    let prevIdx = validIndices[0];
    let nextIdx = validIndices[validIndices.length - 1];
    for (let k = 0; k < validIndices.length - 1; k++) {
      if (validIndices[k] <= i && i <= validIndices[k + 1]) {
        prevIdx = validIndices[k];
        nextIdx = validIndices[k + 1];
        break;
      }
    }
    const t = (i - prevIdx) / (nextIdx - prevIdx);
    result[i] = data[prevIdx] + t * (data[nextIdx] - data[prevIdx]);
  }
  return result;
}

/**
 * Zero-phase moving average filter using forward-backward filtering with odd reflection padding,
 * directly matching Python `scipy.signal.filtfilt(np.ones(w)/w, 1.0, signal, padtype="odd", padlen=15)`.
 *
 * @param signal 1D numerical array
 * @param windowSize Moving average filter window size (default 6)
 */
export function filtfiltMovingAverage(
  signal: number[],
  windowSize: number = 6,
): number[] {
  const n = signal.length;
  if (n <= 2) return [...signal];

  const w = Math.max(1, Math.min(windowSize, n));
  const b = createFilledArray(w, 1 / w);
  const filterOrder = w - 1;
  const desiredPadlen = 3 * filterOrder; // e.g. 15 for w=6
  const padlen = Math.min(desiredPadlen, n - 1);

  if (padlen <= 0) return [...signal];

  // 1. Odd extension (antisymmetric reflection around endpoints)
  // Left: padded[padlen - 1 - i] = 2 * x[0] - x[i + 1]
  // Right: padded[padlen + n + i] = 2 * x[n - 1] - x[n - 2 - i]
  const totalLen = padlen + n + padlen;
  const padded = createFilledArray(totalLen, 0);

  for (let i = 0; i < n; i++) {
    padded[padlen + i] = signal[i];
  }

  for (let i = 0; i < padlen; i++) {
    const leftReflIdx = Math.min(i + 1, n - 1);
    padded[padlen - 1 - i] = 2 * signal[0] - signal[leftReflIdx];

    const rightReflIdx = Math.max(0, n - 2 - i);
    padded[padlen + n + i] = 2 * signal[n - 1] - signal[rightReflIdx];
  }

  // Helper for 1D FIR filter: y[t] = sum_{k=0}^{w-1} b[k] * x[t - k]
  const runFir = (x: number[]): number[] => {
    const len = x.length;
    const y = createFilledArray(len, 0);
    for (let t = 0; t < len; t++) {
      let sum = 0;
      for (let k = 0; k < w; k++) {
        const idx = t - k;
        sum += b[k] * (idx >= 0 ? x[idx] : x[0]);
      }
      y[t] = sum;
    }
    return y;
  };

  // Forward filter
  const yFwd = runFir(padded);

  // Reverse
  yFwd.reverse();

  // Backward filter
  const yBwd = runFir(yFwd);

  // Reverse back
  yBwd.reverse();

  // Extract central unpadded signal
  return yBwd.slice(padlen, padlen + n);
}

export interface PeakDetectionResult {
  indices: number[];
  prominences: number[];
}

/**
 * Finds local peaks (maxima) in a 1D signal with minimum distance separation
 * and topographic prominence thresholding, matching Python `scipy.signal.find_peaks`
 * and `scipy.signal.peak_prominences`.
 *
 * @param signal 1D numerical signal
 * @param distance Minimum sample separation between peaks (default 7)
 * @param prominenceRatio Ratio of mean positive prominence for thresholding (default 0.5)
 */
export function findPeaksWithProminence(
  signal: number[],
  distance: number = 7,
  prominenceRatio: number = 0.5,
): PeakDetectionResult {
  const n = signal.length;
  if (n < 3) {
    return { indices: [], prominences: [] };
  }

  const effectiveDist = Math.max(1, Math.min(distance, Math.floor(n / 2)));

  // 1. Find all local maxima (interior points)
  const candidatePeaks: number[] = [];
  for (let i = 1; i < n - 1; i++) {
    if (signal[i] > signal[i - 1] && signal[i] >= signal[i + 1]) {
      // If flat plateau, check until next strictly lower or higher point
      let r = i + 1;
      while (r < n && signal[r] === signal[i]) {
        r++;
      }
      if (r < n && signal[r] < signal[i]) {
        candidatePeaks.push(Math.floor((i + r - 1) / 2));
        i = r - 1; // skip plateau
      }
      else if (r >= n) {
        break;
      }
    }
  }

  if (candidatePeaks.length === 0) {
    return { indices: [], prominences: [] };
  }

  // 2. Enforce minimum distance separation: prioritize peaks with highest values
  const sortedByHeight = [...candidatePeaks].sort((a, b) => signal[b] - signal[a]);
  const acceptedPeaks: number[] = [];

  for (const peakIdx of sortedByHeight) {
    let tooClose = false;
    for (const accepted of acceptedPeaks) {
      if (Math.abs(peakIdx - accepted) < effectiveDist) {
        tooClose = true;
        break;
      }
    }
    if (!tooClose) {
      acceptedPeaks.push(peakIdx);
    }
  }

  acceptedPeaks.sort((a, b) => a - b);

  // 3. Compute Topographic Prominences for accepted peaks
  const prominences: number[] = [];

  for (const peak of acceptedPeaks) {
    const h = signal[peak];

    // Search left for higher peak / boundary
    let leftBound = 0;
    for (let i = peak - 1; i >= 0; i--) {
      if (signal[i] > h) {
        leftBound = i;
        break;
      }
    }
    let minLeft = h;
    for (let i = leftBound; i <= peak; i++) {
      if (signal[i] < minLeft) minLeft = signal[i];
    }

    // Search right for higher peak / boundary
    let rightBound = n - 1;
    for (let i = peak + 1; i < n; i++) {
      if (signal[i] > h) {
        rightBound = i;
        break;
      }
    }
    let minRight = h;
    for (let i = peak; i <= rightBound; i++) {
      if (signal[i] < minRight) minRight = signal[i];
    }

    const baseLevel = Math.max(minLeft, minRight);
    const prom = Math.max(0, h - baseLevel);
    prominences.push(prom);
  }

  // 4. Filter by prominence threshold: pThresh = prominenceRatio * mean(positive prominences)
  const positiveP = prominences.filter(p => p > 0);
  if (positiveP.length === 0) {
    return { indices: acceptedPeaks, prominences };
  }

  const meanProm = positiveP.reduce((sum, p) => sum + p, 0) / positiveP.length;
  const pThresh = prominenceRatio * meanProm;

  const filteredIndices: number[] = [];
  const filteredProminences: number[] = [];

  for (let i = 0; i < acceptedPeaks.length; i++) {
    if (prominences[i] >= pThresh) {
      filteredIndices.push(acceptedPeaks[i]);
      filteredProminences.push(prominences[i]);
    }
  }

  return {
    indices: filteredIndices,
    prominences: filteredProminences,
  };
}

/**
 * Detects End Systole (ES) and End Diastole (ED) cardiac phases from LV Blood Pool area measurements.
 * Replicates Python backend `find_salient_phases` and `get_cardiac_es_ed_from_area`.
 *
 * - ES (End Systole): Local minima of LV blood pool area (maximal ventricular contraction).
 * - ED (End Diastole): Local maxima of LV blood pool area plus endpoints (maximal ventricular filling).
 *
 * @param lvBpAreaRaw Raw 1D array of LV blood pool area (pixel count) per phase
 * @param excludedIndices Optional indices of excluded phases
 * @param filterWindow Window length for moving average filtfilt (default 6)
 * @param minSeparation Min distance between detected peaks (default 7)
 */
export function detectCardiacEsEdFromArea(
  lvBpAreaRaw: number[],
  excludedIndices: number[] = [],
  filterWindow: number = 6,
  minSeparation: number = 7,
): {
  lvBpAreaSmoothed: number[];
  esIndices: number[];
  edIndices: number[];
  nRR: number;
} {
  const n = lvBpAreaRaw.length;
  if (n === 0) {
    return { lvBpAreaSmoothed: [], esIndices: [], edIndices: [], nRR: 0 };
  }

  const excludedSet = new Set(excludedIndices);

  // 1. Interpolate NaNs / excluded phases across phase axis
  const lvBpAreaForDetection = interpolateNaNs1D(
    lvBpAreaRaw.map((v, i) => (excludedSet.has(i) ? Number.NaN : v)),
  );

  // 2. Zero-phase moving average filter (filtfilt)
  const lvbpSig = filtfiltMovingAverage(lvBpAreaForDetection, filterWindow);

  // 3. Find End-Systole (minima of lvbpSig -> peaks of -lvbpSig)
  const negSig = lvbpSig.map(v => -v);
  const minResult = findPeaksWithProminence(negSig, minSeparation);
  let esIdx = minResult.indices;

  // 4. Find End-Diastole (maxima of lvbpSig)
  const maxResult = findPeaksWithProminence(lvbpSig, minSeparation);
  let edIdx = Array.from(new Set([0, ...maxResult.indices, n - 1])).sort((a, b) => a - b);

  // Helper to shift indices off excluded phases to nearest valid phase
  const moveOffExcluded = (indices: number[]): number[] => {
    if (excludedSet.size === 0 || excludedSet.size >= n) return indices;
    return indices.map((idx) => {
      if (!excludedSet.has(idx)) return idx;
      let bestIdx = idx;
      let minDiff = Infinity;
      for (let j = 0; j < n; j++) {
        if (!excludedSet.has(j)) {
          const diff = Math.abs(j - idx);
          if (diff < minDiff) {
            minDiff = diff;
            bestIdx = j;
          }
        }
      }
      return bestIdx;
    });
  };

  esIdx = Array.from(new Set(moveOffExcluded(esIdx))).sort((a, b) => a - b);
  edIdx = Array.from(new Set(moveOffExcluded(edIdx))).sort((a, b) => a - b);

  // Fallback: If no ES was detected (e.g. very short slice or monotonic signal),
  // pick the global minimum of the valid smoothed signal
  if (esIdx.length === 0 && n >= 2) {
    let minVal = Infinity;
    let minI = 0;
    for (let i = 0; i < n; i++) {
      if (!excludedSet.has(i) && lvbpSig[i] < minVal) {
        minVal = lvbpSig[i];
        minI = i;
      }
    }
    esIdx = [minI];
  }

  const nRR = esIdx.length;

  return {
    lvBpAreaSmoothed: lvbpSig,
    esIndices: esIdx,
    edIndices: edIdx,
    nRR,
  };
}

/**
 * Computes the inverse of a small square matrix using Gaussian elimination.
 */
function invertMatrix(mat: number[][]): number[][] | null {
  const n = mat.length;
  const A = mat.map(row => [...row]);
  const I: number[][] = Array.from({ length: n }, (_, i) =>
    Array.from({ length: n }, (_, j) => (i === j ? 1 : 0)));

  for (let i = 0; i < n; i++) {
    let maxRow = i;
    for (let k = i + 1; k < n; k++) {
      if (Math.abs(A[k][i]) > Math.abs(A[maxRow][i])) {
        maxRow = k;
      }
    }

    if (Math.abs(A[maxRow][i]) < 1e-12) return null;

    // Swap rows
    [A[i], A[maxRow]] = [A[maxRow], A[i]];
    [I[i], I[maxRow]] = [I[maxRow], I[i]];

    const pivot = A[i][i];
    for (let j = 0; j < n; j++) {
      A[i][j] /= pivot;
      I[i][j] /= pivot;
    }

    for (let k = 0; k < n; k++) {
      if (k === i) continue;
      const factor = A[k][i];
      for (let j = 0; j < n; j++) {
        A[k][j] -= factor * A[i][j];
        I[k][j] -= factor * I[i][j];
      }
    }
  }

  return I;
}

/**
 * Savitzky-Golay 1D smoothing filter matching backend `smoothdata_sgolay`.
 * Fits a polynomial of specified degree (default 2) over a sliding window.
 * Handles boundary conditions via boundary polynomial evaluation.
 *
 * @param signal Input 1D numerical signal
 * @param windowSize Filter window length (odd integer, e.g. 5)
 * @param polyOrder Polynomial order (default 2)
 */
export function savitzkyGolaySmooth(
  signal: number[],
  windowSize: number = 5,
  polyOrder: number = 2,
): number[] {
  const n = signal.length;
  if (n <= 2) return [...signal];

  // Adjust window size if needed
  let w = Math.min(windowSize, n);
  if (w % 2 === 0) w -= 1;
  if (w < 3) return [...signal];

  const p = Math.min(polyOrder, w - 1);
  const m = Math.floor(w / 2);

  // Build Vandermonde matrix V of size [w, p + 1]
  const V: number[][] = Array.from({ length: w }, (_, i) => {
    const t = i - m;
    return Array.from({ length: p + 1 }, (_, j) => t ** j);
  });

  // Compute (V^T * V)
  const VtV: number[][] = Array.from({ length: p + 1 }, (_, i) =>
    Array.from({ length: p + 1 }, (_, j) => {
      let sum = 0;
      for (let k = 0; k < w; k++) {
        sum += V[k][i] * V[k][j];
      }
      return sum;
    }));

  const invVtV = invertMatrix(VtV);
  if (!invVtV) return [...signal];

  // Compute S = (V^T * V)^-1 * V^T of size [p + 1, w]
  const S: number[][] = Array.from({ length: p + 1 }, (_, i) =>
    Array.from({ length: w }, (_, j) => {
      let sum = 0;
      for (let k = 0; k < p + 1; k++) {
        sum += invVtV[i][k] * V[j][k];
      }
      return sum;
    }));

  const smoothed = createFilledArray(n, 0);

  // Helper to evaluate polynomial fit at relative offset r in [-m, m]
  const evalAt = (windowSignal: number[], r: number): number => {
    let result = 0;
    for (let j = 0; j < w; j++) {
      let weight = 0;
      for (let k = 0; k < p + 1; k++) {
        weight += (r ** k) * S[k][j];
      }
      result += weight * windowSignal[j];
    }
    return result;
  };

  // Interior points
  for (let i = m; i < n - m; i++) {
    const sub = signal.slice(i - m, i + m + 1);
    smoothed[i] = evalAt(sub, 0);
  }

  // Left boundary points
  const leftWindow = signal.slice(0, w);
  for (let i = 0; i < m; i++) {
    smoothed[i] = evalAt(leftWindow, i - m);
  }

  // Right boundary points
  const rightWindow = signal.slice(n - w, n);
  for (let i = n - m; i < n; i++) {
    smoothed[i] = evalAt(rightWindow, i - (n - 1 - m));
  }

  return smoothed;
}

/**
 * Normalizes an array so its first finite value equals 1.0.
 */
export function normalizeToStart(signal: number[]): number[] {
  if (signal.length === 0) return [];
  const base = signal[0];
  if (!Number.isFinite(base) || Number.isNaN(base) || base === 0) {
    return signal.map(() => 1);
  }
  return signal.map(v => v / base);
}

async function yieldToUI(): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, 0);
  });
}

// ─── Viewport Calculation Pipeline ──────────────────────────────────────────

/**
 * Calculates the signal intensity curves for a given viewport.
 * Extracts the slice's breathhold range, loads slice images and contours,
 * performs 6-sector division per phase, interpolates missing phases,
 * computes LAD, RCA, LCX, and Global territory curves, applies Savitzky-Golay
 * smoothing, and normalizes the curves to start at 1.0.
 */
export async function calculateOcgCurvesForViewport(
  viewerID: string,
  options: OcgCalculationOptions = {},
): Promise<OcgCurvesResult | null> {
  const tStartOverall = performance.now();

  const currentStudyStore = useCurrentStudyStore();
  const viewportStateStore = useViewportStateStore();
  const contourContainerStore = useContourContainerStore();
  const activeStudyStore = useActiveStudyStore();
  const { getContourIdsForOrientation } = useMaskExtractor();

  const numSegments = options.numberSegments ?? 6;
  const sgoKrn = options.sgoKrn ?? 5;
  const onProgress = options.onProgress;

  // 1. Get viewport selection and series/slice indices
  const t0Setup = performance.now();
  const indices = viewportStateStore.getSelectedImageIndices(viewerID);
  const seriesIndex = indices.seriesIdx;
  const sliceIndex = indices.sliceIdx;

  if (seriesIndex < 0 || sliceIndex < 0) {
    console.warn(`[OCGCalculator] Invalid viewport indices for viewer ${viewerID}: series ${seriesIndex}, slice ${sliceIndex}`);
    return null;
  }

  const seriesUID = currentStudyStore.getSeriesUID(seriesIndex) ?? '';
  const allSliceImageUIDs = currentStudyStore.getSliceImageUIDsbyView(viewerID) ?? [];
  if (allSliceImageUIDs.length === 0) {
    console.warn(`[OCGCalculator] No image UIDs found for viewer ${viewerID}`);
    return null;
  }

  // 2. Get SliceObject and Breathhold range
  const sliceObject = activeStudyStore.getStudyObject(String(seriesIndex), sliceIndex);
  let startPhase = 0;
  let endPhase = allSliceImageUIDs.length - 1;

  try {
    const breathhold = sliceObject?.getBreathhold();
    if (breathhold) {
      startPhase = Math.max(0, breathhold.getStart());
      endPhase = Math.min(allSliceImageUIDs.length - 1, breathhold.getEnd());
    }
  }
  catch {
    // If no breathhold state is defined, use the full phase range
  }

  const breathholdImageUIDs = allSliceImageUIDs.slice(startPhase, endPhase + 1);
  const nPhases = breathholdImageUIDs.length;
  if (nPhases === 0) {
    console.warn(`[OCGCalculator] Empty breathhold range [${startPhase}, ${endPhase}]`);
    return null;
  }

  onProgress?.({
    current: 0,
    total: nPhases,
    percent: 0,
    phaseIndex: startPhase,
    stage: `Preparing ${nPhases} phases...`,
  });
  await yieldToUI();

  const orientation
    = seriesClassification.getClassificationByViewport(viewerID)?.orientation ?? Orientation.Sax;

  // 3. Find reference insertion point across slice as fallback
  const isLax = isLaxOrientation(orientation);
  const insertionPointLabel = isLax
    ? ContourLabel.LaxLvEndoBasalPoint
    : ContourLabel.RvInsertion;

  let fallbackInsertionPoint: Point | null = null;
  for (const uid of breathholdImageUIDs) {
    const container = contourContainerStore.getContourContainer(uid);
    if (!container) continue;
    const insertionContour = container.getContour({
      label: insertionPointLabel,
      id: encodeContourLabelItemId([0]),
    });
    const poly = insertionContour?.getPolygonScaled();
    if (poly && poly.length > 0) {
      fallbackInsertionPoint = poly[0];
      break;
    }
  }

  const tSetupDuration = performance.now() - t0Setup;

  // 4. Extract sector-wise, global signal intensities, and blood pool area per phase
  const siAHA16Raw: number[][] = Array.from({ length: nPhases }, () =>
    createFilledArray(numSegments, Number.NaN));
  const siGlobalRaw: number[] = createFilledArray(nPhases, Number.NaN);
  const lvBpAreaRaw: number[] = createFilledArray(nPhases, Number.NaN);
  const excludedPhaseIndices: number[] = [];
  const triggerTimes: number[] = [];
  const perPhaseMyoMaskData: Array<{
    imageUID: string;
    coords: [number, number][];
    rawValues: number[];
  } | null> = createFilledArray<{
    imageUID: string;
    coords: [number, number][];
    rawValues: number[];
  } | null>(nPhases, null);

  let tVtkTotal = 0;
  let tMasksTotal = 0;
  let tBlobDivisionTotal = 0;
  const t0PhaseLoop = performance.now();

  for (let phaseIdx = 0; phaseIdx < nPhases; phaseIdx++) {
    const globalPhaseIdx = startPhase + phaseIdx;
    const imageUID = breathholdImageUIDs[phaseIdx];

    onProgress?.({
      current: phaseIdx + 1,
      total: nPhases,
      percent: Math.round(((phaseIdx + 1) / nPhases) * 100),
      phaseIndex: globalPhaseIdx,
      stage: `Phase ${phaseIdx + 1}/${nPhases}`,
    });

    // Yield to the browser event loop so Vue updates the progress bar DOM
    await yieldToUI();

    // Trigger time from DICOM metadata
    const info = currentStudyStore.imageInfoDicom[imageUID];
    triggerTimes.push(info?.TriggerTime ?? globalPhaseIdx * 50);

    // Check if phase is excluded
    if (sliceObject && sliceObject.isImageExcluded(imageUID)) {
      excludedPhaseIndices.push(phaseIdx);
      continue;
    }

    const container = contourContainerStore.getContourContainer(imageUID);
    if (!container) {
      excludedPhaseIndices.push(phaseIdx);
      continue;
    }

    // Load VTK image data (timed)
    const t0Vtk = performance.now();
    const imageData = await currentStudyStore.fetchOrGetVtkImageData(imageUID);
    tVtkTotal += performance.now() - t0Vtk;

    if (!imageData) {
      excludedPhaseIndices.push(phaseIdx);
      continue;
    }

    // Contour masks extraction (timed)
    const t0Masks = performance.now();
    const { myoContourId, endoContourId, epiContourId } = getContourIdsForOrientation(
      orientation,
      viewerID,
    );

    let myoPixels: PointSiArray = [];
    try {
      myoPixels = container.getContourMaskCompleteIn(myoContourId, imageData);
    }
    catch {
      myoPixels = [];
    }

    let endoPixels: PointSiArray = [];
    try {
      endoPixels = container.getContourMaskCompleteIn(endoContourId, imageData);
    }
    catch {
      endoPixels = [];
    }

    // Track LV blood pool area (pixel count of endocardial mask)
    if (endoPixels.length > 0) {
      lvBpAreaRaw[phaseIdx] = endoPixels.length;
    }

    // If myoFusion mask is empty, fallback to epi mask minus endo mask
    if (myoPixels.length === 0) {
      try {
        const epiPixels = container.getContourMaskCompleteIn(epiContourId, imageData);
        if (epiPixels.length > 0) {
          const endoSet = new Set(endoPixels.map(p => `${p.x},${p.y}`));
          myoPixels = epiPixels.filter(p => !endoSet.has(`${p.x},${p.y}`));
        }
      }
      catch {
        myoPixels = [];
      }
    }
    else if (endoPixels.length > 0) {
      // Ensure pure myocardium by subtracting endocardial bloodpool
      const endoSet = new Set(endoPixels.map(p => `${p.x},${p.y}`));
      const pure = myoPixels.filter(p => !endoSet.has(`${p.x},${p.y}`));
      if (pure.length > 0) myoPixels = pure;
    }
    tMasksTotal += performance.now() - t0Masks;

    if (myoPixels.length === 0) {
      excludedPhaseIndices.push(phaseIdx);
      continue;
    }

    // Blob division and signal intensity sampling (timed)
    const t0Blob = performance.now();
    const blobPoints: Point[] = [...endoPixels, ...myoPixels];

    const insertionContour = container.getContour({
      label: insertionPointLabel,
      id: encodeContourLabelItemId([0]),
    });
    const insertionPoly = insertionContour?.getPolygonScaled();
    const rvPoint = (insertionPoly && insertionPoly.length > 0)
      ? insertionPoly[0]
      : (fallbackInsertionPoint ?? { x: blobPoints[0].x, y: blobPoints[0].y - 100 });

    const { getSegment } = divideBlobOptimized(blobPoints, numSegments, rvPoint);

    const segmentPixelValues: number[][] = Array.from({ length: numSegments }, () => []);
    const allMyoPixelValues: number[] = [];
    const rawMyoCoords: [number, number][] = [];
    const rawMyoValues: number[] = [];
    const scalars = imageData.getPointData()?.getScalars()?.getData() as TypedArray | undefined;
    const dims = imageData.getDimensions();
    const width = dims[0];
    const height = dims[1];

    for (let i = 0; i < myoPixels.length; i++) {
      const p = myoPixels[i];
      if (p.x < 0 || p.x >= width || p.y < 0 || p.y >= height) continue;

      let val = p.si;
      if (scalars) {
        val = scalars[p.y * width + p.x];
      }

      allMyoPixelValues.push(val);
      rawMyoCoords.push([p.y, p.x]);
      rawMyoValues.push(val);

      const segIndex = getSegment(p.x, p.y) - 1;
      if (segIndex >= 0 && segIndex < numSegments) {
        segmentPixelValues[segIndex].push(val);
      }
    }

    perPhaseMyoMaskData[phaseIdx] = {
      imageUID,
      coords: rawMyoCoords,
      rawValues: rawMyoValues,
    };

    // Extract sector median signal intensities and whole myocardium global median for this phase
    for (let s = 0; s < numSegments; s++) {
      siAHA16Raw[phaseIdx][s] = calculateMedian(segmentPixelValues[s]);
    }
    siGlobalRaw[phaseIdx] = calculateMedian(allMyoPixelValues);
    tBlobDivisionTotal += performance.now() - t0Blob;
  }

  const tPhaseLoopDuration = performance.now() - t0PhaseLoop;

  // 5. Interpolate missing/excluded phase NaNs (timed)
  const t0Interp = performance.now();
  const siAHA16 = interpolateNaNs(siAHA16Raw);
  const siGlobal = interpolateNaNs(siGlobalRaw.map(v => [v])).map(r => r[0]);
  const tInterpDuration = performance.now() - t0Interp;

  // 6. Calculate territory curves (timed)
  const t0Territory = performance.now();
  const siLAD: number[] = createFilledArray(nPhases, Number.NaN);
  const siRCA: number[] = createFilledArray(nPhases, Number.NaN);
  const siLCx: number[] = createFilledArray(nPhases, Number.NaN);

  for (let p = 0; p < nPhases; p++) {
    siLAD[p] = calculateMean([siAHA16[p][0], siAHA16[p][1]]);
    siRCA[p] = calculateMean([siAHA16[p][2], siAHA16[p][3]]);
    siLCx[p] = calculateMean([siAHA16[p][4], siAHA16[p][5]]);
  }
  const tTerritoryDuration = performance.now() - t0Territory;

  // 7. Savitzky-Golay smoothing (timed)
  const t0Smooth = performance.now();
  const siLADSmoothed = savitzkyGolaySmooth(siLAD, sgoKrn);
  const siRCASmoothed = savitzkyGolaySmooth(siRCA, sgoKrn);
  const siLCxSmoothed = savitzkyGolaySmooth(siLCx, sgoKrn);
  const siGlobalSmoothed = savitzkyGolaySmooth(siGlobal, sgoKrn);
  const tSmoothDuration = performance.now() - t0Smooth;

  // 8. Normalization to baseline (start at 1.0) (timed)
  const t0Norm = performance.now();
  const siLADNormalized = normalizeToStart(siLADSmoothed);
  const siRCANormalized = normalizeToStart(siRCASmoothed);
  const siLCxNormalized = normalizeToStart(siLCxSmoothed);
  const siGlobalNormalized = normalizeToStart(siGlobalSmoothed);
  const tNormDuration = performance.now() - t0Norm;

  // 9. Detect End-Systole and End-Diastole salient phases from LV Blood Pool area curve
  const t0Salient = performance.now();
  const cardiacPhases = detectCardiacEsEdFromArea(lvBpAreaRaw, excludedPhaseIndices);
  const tSalientDuration = performance.now() - t0Salient;

  // 10. Compute ED Myocardium Normalization Factor & Generate Dynamic Signal Intensity Masks
  const firstPhasePositiveMyo = perPhaseMyoMaskData[0]?.rawValues.filter(v => v > 0) ?? [];
  let edMyoVal = calculateMedian(firstPhasePositiveMyo);
  if (!Number.isFinite(edMyoVal) || edMyoVal <= 0) {
    for (let p = 1; p < nPhases; p++) {
      const nextPositive = perPhaseMyoMaskData[p]?.rawValues.filter(v => v > 0) ?? [];
      if (nextPositive.length > 0) {
        edMyoVal = calculateMedian(nextPositive);
        break;
      }
    }
  }
  if (!Number.isFinite(edMyoVal) || edMyoVal <= 0) {
    edMyoVal = 1.0;
  }

  // Compute Mask-Matched curves (curves normalized by Global Phase 0 ED Myocardium median, matching the mask overlay)
  const normalizeByEdMyo = (signal: number[]): number[] => {
    if (signal.length === 0) return [];
    if (!Number.isFinite(edMyoVal) || edMyoVal <= 0) return [...signal];
    return signal.map(v => (Number.isFinite(v) ? v / edMyoVal : v));
  };

  const siLADMaskMatched = normalizeByEdMyo(siLADSmoothed);
  const siRCAMaskMatched = normalizeByEdMyo(siRCASmoothed);
  const siLCxMaskMatched = normalizeByEdMyo(siLCxSmoothed);
  const siGlobalMaskMatched = normalizeByEdMyo(siGlobalSmoothed);

  // Calculate global min and max normalized signal intensities across ALL phases
  let globalMinNorm = Infinity;
  let globalMaxNorm = -Infinity;

  for (let p = 0; p < nPhases; p++) {
    const pData = perPhaseMyoMaskData[p];
    if (!pData || pData.rawValues.length === 0) continue;

    for (let i = 0; i < pData.rawValues.length; i++) {
      const normVal = edMyoVal > 0 ? pData.rawValues[i] / edMyoVal : pData.rawValues[i];
      if (Number.isFinite(normVal)) {
        if (normVal < globalMinNorm) globalMinNorm = normVal;
        if (normVal > globalMaxNorm) globalMaxNorm = normVal;
      }
    }
  }

  if (!Number.isFinite(globalMinNorm) || !Number.isFinite(globalMaxNorm) || globalMinNorm >= globalMaxNorm) {
    globalMinNorm = 0.5;
    globalMaxNorm = 1.5;
  }

  const combinedSliceKey = `${seriesUID}_${sliceIndex}`;
  const generatedMasks: BiomarkerMask[] = [];

  for (let p = 0; p < nPhases; p++) {
    const pData = perPhaseMyoMaskData[p];
    if (!pData || pData.rawValues.length === 0) continue;

    const normValues = pData.rawValues.map(v => (edMyoVal > 0 ? v / edMyoVal : v));

    const mask = new BiomarkerMask(
      true, // isDynamic
      'SignalIntensity',
      pData.imageUID,
      {
        values: normValues,
        coords: pData.coords,
      },
      combinedSliceKey,
      null,
      [globalMinNorm, globalMaxNorm],
      edMyoVal,
      pData.rawValues,
    );
    generatedMasks.push(mask);
  }

  // 11. Populate / Upsert into BiomarkerContainer
  try {
    const analysisStore = useAnalysisStore();
    const biomarkerContainer = analysisStore.getBiomarkerContainer();

    if (generatedMasks.length > 0) {
      biomarkerContainer.addBiomarkerMasks(generatedMasks);
    }

    biomarkerContainer.addAnalyzedSeries({
      seriesUID,
      seriesIdx: seriesIndex,
      sliceIndex,
      analysisSliceIndex: sliceIndex,
    });

    biomarkerContainer.addBiomarkerCurves([
      {
        biomarkerName: 'MYOSI',
        territory: 'Global',
        normalization: 'none',
        data: siGlobalNormalized,
        sliceIndex: combinedSliceKey,
        interpolatedIndexes: null,
      },
      {
        biomarkerName: 'MYOSI',
        territory: 'LAD',
        normalization: 'none',
        data: siLADNormalized,
        sliceIndex: combinedSliceKey,
        interpolatedIndexes: null,
      },
      {
        biomarkerName: 'MYOSI',
        territory: 'RCA',
        normalization: 'none',
        data: siRCANormalized,
        sliceIndex: combinedSliceKey,
        interpolatedIndexes: null,
      },
      {
        biomarkerName: 'MYOSI',
        territory: 'LCx',
        normalization: 'none',
        data: siLCxNormalized,
        sliceIndex: combinedSliceKey,
        interpolatedIndexes: null,
      },
    ]);

    if (cardiacPhases.edIndices.length > 0 || cardiacPhases.esIndices.length > 0) {
      biomarkerContainer.addSalientIndexes([
        {
          type: 'ED',
          data: cardiacPhases.edIndices,
          sliceIndex: combinedSliceKey,
        },
        {
          type: 'ES',
          data: cardiacPhases.esIndices,
          sliceIndex: combinedSliceKey,
        },
      ]);
    }

    const curDynamicMarker = viewportStateStore.getSelectedMask(viewerID, 'Dynamic');
    if (!curDynamicMarker) {
      viewportStateStore.setSelectedMarker(viewerID, 'Dynamic', 'SignalIntensity');
    }
  }
  catch (e) {
    console.warn('[OCGCalculator] Failed to populate biomarker container:', e);
  }

  const tTotalOverall = performance.now() - tStartOverall;

  // ─── Log Operation Timings & Bottlenecks ────────────────────────────────
  console.group(`[OCGCalculator Benchmark] Viewport: ${viewerID} | Series ${seriesIndex + 1} Slice ${sliceIndex + 1} (${nPhases} phases)`);
  console.log(`1. Setup & Breathhold Discovery: ${tSetupDuration.toFixed(2)} ms`);
  console.log(`2. Per-Phase Computation Loop (Total ${tPhaseLoopDuration.toFixed(2)} ms):`);
  console.log(`   - VTK Image Fetch/Decompress: ${tVtkTotal.toFixed(2)} ms (avg ${(tVtkTotal / Math.max(1, nPhases)).toFixed(2)} ms/phase)`);
  console.log(`   - Contour Masks Extraction:  ${tMasksTotal.toFixed(2)} ms (avg ${(tMasksTotal / Math.max(1, nPhases)).toFixed(2)} ms/phase)`);
  console.log(`   - 6-Sector Blob Division:     ${tBlobDivisionTotal.toFixed(2)} ms (avg ${(tBlobDivisionTotal / Math.max(1, nPhases)).toFixed(2)} ms/phase)`);
  console.log(`3. Temporal NaN Interpolation:    ${tInterpDuration.toFixed(2)} ms`);
  console.log(`4. Territory Curves Mean:         ${tTerritoryDuration.toFixed(2)} ms`);
  console.log(`5. Savitzky-Golay Filter:         ${tSmoothDuration.toFixed(2)} ms`);
  console.log(`6. Baseline Normalization:        ${tNormDuration.toFixed(2)} ms`);
  console.log(`7. ES/ED Salient Phase Detect:    ${tSalientDuration.toFixed(2)} ms (ES: ${cardiacPhases.esIndices.join(', ')}, ED: ${cardiacPhases.edIndices.join(', ')})`);
  console.log(`8. Signal Intensity Mask Count:   ${generatedMasks.length} masks (edMyoVal: ${edMyoVal.toFixed(2)})`);
  console.log(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
  console.log(`Total Pipeline Execution Time:    ${tTotalOverall.toFixed(2)} ms (${(tTotalOverall / 1000).toFixed(3)} s)`);
  console.groupEnd();

  return {
    viewerID,
    seriesUID,
    seriesIndex,
    sliceIndex,
    startPhase,
    endPhase,
    nPhases,
    imageUIDs: breathholdImageUIDs,
    triggerTimes,
    excludedPhaseIndices,
    edMyoVal,
    siAHA16,
    siLAD,
    siRCA,
    siLCx,
    siGlobal,
    siLADSmoothed,
    siRCASmoothed,
    siLCxSmoothed,
    siGlobalSmoothed,
    siLADNormalized,
    siRCANormalized,
    siLCxNormalized,
    siGlobalNormalized,
    siLADMaskMatched,
    siRCAMaskMatched,
    siLCxMaskMatched,
    siGlobalMaskMatched,
    lvBpArea: lvBpAreaRaw,
    lvBpAreaSmoothed: cardiacPhases.lvBpAreaSmoothed,
    esIndices: cardiacPhases.esIndices,
    edIndices: cardiacPhases.edIndices,
    nRR: cardiacPhases.nRR,
  };
}

// ─── Pinia Store Definition ─────────────────────────────────────────────────

export const useOCGCalculatorStore = defineStore('ocg-calculator-store', {
  state: (): OcgCalculatorState => ({
    ocgCurvesCache: {},
    ocgCurvesByViewport: {},
    isCalculating: false,
    curveDisplayMode: 'normalized',
    isNormalized: true,
    progress: {
      current: 0,
      total: 0,
      percent: 0,
      phaseIndex: 0,
      stage: '',
    },
    error: null as string | null,
  }),
  getters: {
    getCurves: state => (seriesIndex: number, sliceIndex: number): OcgCurvesResult | undefined => {
      const key = `${seriesIndex}_${sliceIndex}`;
      return state.ocgCurvesCache[key];
    },
    getCurvesBySeriesAndSlice: state => (seriesIndex: number, sliceIndex: number): OcgCurvesResult | undefined => {
      const key = `${seriesIndex}_${sliceIndex}`;
      return state.ocgCurvesCache[key];
    },
    getCurvesByViewport: state => (viewerID: string): OcgCurvesResult | undefined => {
      const viewportStateStore = useViewportStateStore();
      const indices = viewportStateStore.getSelectedImageIndices(viewerID);
      if (indices && typeof indices.seriesIdx === 'number' && typeof indices.sliceIdx === 'number') {
        const key = `${indices.seriesIdx}_${indices.sliceIdx}`;
        if (state.ocgCurvesCache[key]) {
          return state.ocgCurvesCache[key];
        }
      }
      return state.ocgCurvesByViewport[viewerID];
    },
    getProgress: state => state.progress,
    getCurveDisplayMode: state => state.curveDisplayMode,
    getIsNormalized: state => state.curveDisplayMode === 'normalized',
  },
  actions: {
    setCurveDisplayMode(mode: OcgCurveDisplayMode) {
      this.curveDisplayMode = mode;
      this.isNormalized = mode === 'normalized';
    },
    setIsNormalized(normalized: boolean) {
      this.curveDisplayMode = normalized ? 'normalized' : 'raw';
      this.isNormalized = normalized;
    },
    setCurves(seriesIndex: number, sliceIndex: number, result: OcgCurvesResult) {
      const key = `${seriesIndex}_${sliceIndex}`;
      this.ocgCurvesCache[key] = result;
      if (result.viewerID) {
        this.ocgCurvesByViewport[result.viewerID] = result;
      }
    },
    async calculate(
      viewerID: string,
      options?: OcgCalculationOptions,
    ): Promise<OcgCurvesResult | null> {
      this.isCalculating = true;
      this.error = null;
      this.progress = {
        current: 0,
        total: 0,
        percent: 0,
        phaseIndex: 0,
        stage: 'Starting calculation...',
      };

      try {
        const result = await calculateOcgCurvesForViewport(viewerID, {
          ...options,
          onProgress: (p) => {
            this.progress = p;
            options?.onProgress?.(p);
          },
        });
        if (result) {
          const key = `${result.seriesIndex}_${result.sliceIndex}`;
          this.ocgCurvesCache[key] = result;
          this.ocgCurvesByViewport[viewerID] = result;
        }
        return result;
      }

      catch (err: any) {
        const message = err instanceof Error ? err.message : 'Failed to calculate OCG curves';
        this.error = message;
        console.error('[OCGCalculatorStore] Calculation error:', err);
        return null;
      }
      finally {
        this.isCalculating = false;
      }
    },
    reset() {
      this.ocgCurvesCache = {};
      this.ocgCurvesByViewport = {};
      this.isCalculating = false;
      this.curveDisplayMode = 'normalized';
      this.isNormalized = true;
      this.progress = {
        current: 0,
        total: 0,
        percent: 0,
        phaseIndex: 0,
        stage: '',
      };
      this.error = null;
    },
  },
});
